// Location variants on Admin > Documents: each group, its versions and who they apply to, and
// what each location actually reads from it (core forAudience, the same rule answers use).

import { forAudience, type HrDocument, type ReviewStatus } from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, AUDIENCES, type ApplicabilityValue, type Audience, type SharePointSource } from "@ask-hr/core/sources";

type Doc = HrDocument & { status: ReviewStatus };
type Member = Doc & { applicability: ApplicabilityValue; who: ApplicabilityValue };

const AUDIENCE_NAMES: Record<Audience, string> = {
  "us-ca": "California",
  "us-other": "US outside California",
  canada: "Canada",
};

const short = (name: string) => name.replace(/\.(pdf|docx)$/i, "");

export function VariantGroups({
  docs,
  sources,
  onShow,
}: {
  docs: Doc[];
  sources: SharePointSource[];
  onShow: (group: string) => void;
}) {
  const sourceOf = new Map(sources.map((s) => [s.id, s]));
  const groups = new Map<string, Member[]>();
  for (const d of docs) {
    if (!d.variantGroup || d.status === "removed" || d.status === "excluded") continue;
    const who = d.applicability ?? sourceOf.get(d.sourceId)?.applicability;
    if (!who) continue;
    groups.set(d.variantGroup, [...(groups.get(d.variantGroup) ?? []), { ...d, applicability: who, who }]);
  }
  const names = [...groups.keys()].sort((a, b) => a.localeCompare(b));

  return (
    <section className="panel" aria-labelledby="variants-h">
      <h2 id="variants-h">Location variants</h2>
      <p className="sub">
        Versions of one document for different places. An employee gets the version whose Applies to is their location; if
        there isn't one, the version for All employees. Documents outside a group work as before.
      </p>
      {names.length === 0 ? (
        <p className="empty">
          No groups yet. In Documents below, tick the versions (for example the general and California employee guides) and
          choose Group as location variants.
        </p>
      ) : (
        <div className="variant-groups">
          {names.map((name) => {
            const members = groups.get(name) ?? [];
            const reads = AUDIENCES.map((a) => [a, forAudience(members, a)] as const);
            const differs = new Set(members.map((m) => m.who)).size > 1;
            return (
              <div key={name} className="variant-group">
                <div className="variant-group-head">
                  <h3>{name}</h3>
                  <button type="button" className="linkbtn" onClick={() => onShow(name)}>
                    Show in documents
                  </button>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Version</th>
                      <th scope="col">Applies to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id}>
                        <td>
                          {short(m.name)}
                          {m.status !== "approved" ? <span className="muted small"> · not approved yet</span> : null}
                        </td>
                        <td>{APPLICABILITY_LABELS[m.who]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ul className="variant-reads" aria-label={`Who reads what from ${name}`}>
                  {reads.map(([a, docsFor]) => (
                    <li key={a}>
                      <span className="muted">{AUDIENCE_NAMES[a]} reads</span>{" "}
                      {docsFor.length ? <strong>{docsFor.map((d) => short(d.name)).join(" + ")}</strong> : <em>nothing from this group</em>}
                    </li>
                  ))}
                </ul>
                {!differs ? (
                  <p className="notice small">
                    Every version applies to the same people, so everyone reads all of them. Set each version's Applies to
                    (for example the California one to California only).
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <p className="muted small">What employees read changes when you publish.</p>
    </section>
  );
}
